import * as THREE from "three";
import { animationCompositionIssues, compositionHands, type AnimationCompositionV1, type CompositionAction, type CompositionInterrupt } from "@hyperlight/shared";
import { compileHumanoidAnimation } from "./humanoidAnimation";

export interface CompositionState {
  aiming: boolean;
  reloading: boolean;
  airborne: boolean;
  equipment: string | null;
  compatible: boolean;
  action: boolean;
  interaction: boolean;
}
interface CompiledAction {
  def: CompositionAction;
  tracks: { bone: string; sample: THREE.Interpolant }[];
}
interface Playback {
  action: CompiledAction;
  time: number;
  weight: number;
  equipment: string | null;
  exiting: number | null;
  exitWeight: number;
}
const smooth = (v: number): number => { const t = Math.max(0, Math.min(1, v)); return t * t * (3 - 2 * t); };

/** Rotation overrides consume the final solved carry as their reference, never
 * last frame's gesture. The reference can keep moving while an action fades. */
export class AnimationComposition {
  private actions = new Map<string, CompiledAction>();
  private active: Playback | null = null;
  private reason: string | null = null;
  private readonly q = new THREE.Quaternion();
  private profileId: string | null = null;
  private movement: AnimationCompositionV1["movement"] | null = null;
  get playing(): boolean { return this.active !== null; }

  install(value: AnimationCompositionV1 | null): void {
    const issues = value ? animationCompositionIssues(value) : [];
    if (issues.length) throw new Error(issues.join("; "));
    // Compile before replacing the live document: invalid imports are atomic.
    const next = new Map<string, CompiledAction>();
    for (const input of value?.actions ?? []) {
      const def = structuredClone(input);
      next.set(def.id, { def, tracks: compileHumanoidAnimation(def.source).tracks.map(track => ({
        bone: /^\.bones\[([^\]]+)\]/.exec(track.name)![1], sample: track.createInterpolant(),
      })) });
    }
    this.actions = next;
    this.profileId = value?.id ?? null;
    this.movement = value?.movement ?? null;
    this.active = null;
    this.reason = null;
  }

  private blocker(a: CompositionAction, state: CompositionState, equipment = state.equipment): CompositionInterrupt | null {
    const conditions: Record<CompositionInterrupt, boolean> = {
      aim: state.aiming, reload: state.reloading, airborne: state.airborne,
      equipment: !state.compatible || equipment !== state.equipment,
      action: state.action, interaction: state.interaction,
    };
    return a.interrupts.find(key => conditions[key]) ?? null;
  }

  play(id: string, state: CompositionState): boolean {
    const action = this.actions.get(id);
    if (!action) { this.reason = `missing action: ${id}`; return false; }
    const blocked = this.blocker(action.def, state);
    if (blocked) { this.reason = blocked; return false; }
    // One gesture slot in V1. Avoid abrupt replacement: equal/lower priority is
    // rejected; a higher priority request fades the old action first. Caller
    // retries after completion rather than leaving a stale queued request.
    if (this.active) {
      if (action.def.priority > this.active.action.def.priority) this.cancel("superseded");
      return false;
    }
    this.active = { action, time: 0, weight: 0, equipment: state.equipment, exiting: null, exitWeight: 0 };
    this.reason = null;
    return true;
  }

  cancel(reason = "cancelled"): void {
    if (!this.active || this.active.exiting !== null) return;
    this.active.exiting = 0;
    this.active.exitWeight = this.active.weight;
    this.reason = reason;
  }

  /** Explicit authoring seek; runtime requests use play and never restart a
   * cancelled action merely because an input remains held. */
  seek(id: string, time: number, state: CompositionState): boolean {
    if (!Number.isFinite(time)) return false;
    this.active = null;
    if (!this.play(id, state)) return false;
    this.active!.time = Math.max(0, Math.min(time, this.active!.action.def.source.duration));
    this.step(0, state);
    return true;
  }

  step(dt: number, state: CompositionState): void {
    if (!Number.isFinite(dt) || dt < 0) throw new Error("composition dt must be finite and nonnegative");
    const p = this.active;
    if (!p) return;
    const blocked = this.blocker(p.action.def, state, p.equipment);
    if (blocked) this.cancel(blocked);
    const a = p.action.def;
    if (p.exiting !== null) {
      p.exiting += dt;
      // Hold the sampled gesture while blending back to the CURRENT carry.
      p.weight = p.exitWeight * (1 - smooth(p.exiting / a.fadeOut));
      if (p.exiting >= a.fadeOut || p.weight <= 1e-8) this.active = null;
    } else {
      p.time = Math.min(a.source.duration, p.time + dt);
      p.weight = smooth(p.time / a.fadeIn) * smooth((a.source.duration - p.time) / a.fadeOut);
      if (p.time >= a.source.duration) this.active = null;
    }
  }

  apply(boneByName: ReadonlyMap<string, THREE.Bone>): void {
    const p = this.active;
    if (!p || p.weight <= 0) return;
    for (const track of p.action.tracks) {
      const bone = boneByName.get(track.bone);
      if (!bone) continue;
      const value = track.sample.evaluate(p.time);
      this.q.fromArray(value).normalize();
      bone.quaternion.slerp(this.q, p.weight);
    }
  }

  diagnostics() {
    const p = this.active;
    const hands = p ? compositionHands(p.action.def.mask) : [];
    return {
      profile: this.profileId, movement: this.movement, action: p?.action.def.id ?? null,
      time: p?.time ?? 0, weight: p?.weight ?? 0, mask: p?.action.def.mask ?? null,
      interrupt: this.reason,
      left: { owner: hands.includes("left") ? p!.action.def.id : "hold", holdWeight: hands.includes("left") ? 1 - p!.weight : 1 },
      right: { owner: hands.includes("right") ? p!.action.def.id : "hold", holdWeight: hands.includes("right") ? 1 - p!.weight : 1 },
    };
  }
}
